import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import type { ResolvedOutfittingTile } from '@adapters/prismic/types/outfittingTile';
import type { AlgonomyOutfit } from '@components/global/Recommendations/outfits';
import OutfitCard from '../OutfitCard';
import LoadingSkeleton from './LoadingSkeleton';
import SimpleLink from '_components/UI/SimpleLink';

import styles from './OutfittingTile.module.scss';

export interface OutfittingTileProps {
  config?: ResolvedOutfittingTile | null;
  outfits: AlgonomyOutfit[];
  isLoading?: boolean;
}

/** Maximum number of outfits rendered in the section. */
const MAX_OUTFITS = 3;

const OutfittingTile = ({
  config,
  outfits,
  isLoading = false,
}: OutfittingTileProps) => {
  const { t } = useTranslation('common');

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (outfits.length === 0) {
    return null;
  }

  const ariaLabel = config?.heading || t('outfitting.sectionAriaLabel');
  const backgroundImage = config?.backgroundImage;

  return (
    <section className={styles.root} aria-label={ariaLabel}>
      <div className={styles.tile}>
        <div className={styles.info}>
          {config?.heading && (
            <h2 className={styles.heading}>{config.heading}</h2>
          )}
          {config?.supportingCopy && (
            <p className={styles.copy}>{config.supportingCopy}</p>
          )}
          {config?.ctaLabel && config?.ctaHref && (
            <SimpleLink
              variant="secondary"
              className={styles.cta}
              href={config.ctaHref}
            >
              {config.ctaLabel}
            </SimpleLink>
          )}
        </div>

        <div className={styles.media}>
          {backgroundImage?.url && (
            <div className={styles.imageWrapper}>
              <Image
                src={backgroundImage.url}
                alt={backgroundImage.alt ?? ''}
                fill
                className={styles.image}
                sizes="(min-width: 988px) 640px, 100vw"
              />
              <span className={styles.overlay} aria-hidden="true" />
            </div>
          )}

          <div className={styles.cards}>
            {outfits.slice(0, MAX_OUTFITS).map((outfit) => (
              <OutfitCard
                key={outfit.items[0]?.id ?? outfit.title}
                outfit={outfit}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OutfittingTile;
